import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DispersaoUrgenciaEmergencia } from '@gestor-web/@core/api/ords/model';
import { LayoutActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

import { Store } from '@ngrx/store';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { NotificationService, GeralService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import { buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, first, map, switchMap, takeUntil } from 'rxjs/operators';
import { DispersaoUrgenciaEmergenciaService } from './dispersao-urgencia-emergencia.service';

const GRID_KEY = 'ANALISES.DISPERSAO_URGENCIA_EMERGENCIA';

const COLUMNS: ColDef[] = [
  {
    headerName: 'UF',
    field: 'sg_uf',
    width: 80,
  },
  {
    headerName: 'Cidade',
    field: 'nm_municipio',
    width: 100,
  },
  {
    headerName: 'Verificação U/E no RPS',
    field: 'verificacao',
    width: 200,
  },
  {
    headerName: 'CNPJ',
    field: 'nr_cnpj',
    type: 'cnpj',
    width: 140,
  },
  {
    headerName: 'CNES',
    field: 'nr_cnes',
    width: 100,
  },
  {
    headerName: 'Razão social',
    field: 'nm_prestador',
    width: 150,
  },
  {
    headerName: 'Credenciado U/E',
    field: 'ib_credenciado',
    width: 130,
  },
  {
    headerName: 'Contratualização',
    field: 'tp_contratualizacao',
    width: 155,
  },
  {
    headerName: 'Atributo de qualidade',
    field: 'sg_acreditacao',
    width: 150,
  },
  {
    headerName: 'Qtde. estabelecimento',
    field: 'qt_estabelecimento',
    hide: true,
  },
];

@Component({
    templateUrl: './dispersao-urgencia-emergencia.component.html',
    styleUrls: ['./dispersao-urgencia-emergencia.component.scss'],
    providers: [DispersaoUrgenciaEmergenciaService, DecimalPipe],
    standalone: false
})
export class DispersaoUrgenciaEmergenciaComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<Row>;

  gridOptions: GridOptions<Row>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  anoBase: number;
  anoBaseMinimo: number;
  anoAtual: number;
  competenciaRps: string;

  rowData: Row[];

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private permissaoService: PermissaoService,
    private service: DispersaoUrgenciaEmergenciaService,
    private decimalPipe: DecimalPipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private geralService: GeralService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  ngOnInit(): void {
    this.anoAtual = new Date().getFullYear();

    // Aguarda a operadora estar carregada antes de consultar endpoints que dependem do regans.
    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        switchMap(([operadora]) => {
          this.operadora = operadora.cd_registro_ans;
          this.operadoraNome = operadora.sg_operadora;

          return combineLatest([this.service.getMaxAnoBase(this.operadora), this.geralService.getAnoBaseVigente()]);
        }),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe({
        next: ([maxAnoBase, anoBaseVigente]) => {
          this.anoBaseMinimo = maxAnoBase;
          this.anoBase = anoBaseVigente;

          this.service
            .getCompetenciaRpsGoApi(this.operadora)
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe({
              next: competenciaData => {
                const competenciaValue = competenciaData?.competencia_rps;
                this.competenciaRps = competenciaValue ? competenciaValue.toString() : 'N/A';
              },
              error: error => {
                console.error('Erro ao buscar competência RPS:', error);
                this.competenciaRps = '-';
              },
            });

          setTimeout(() => this.pageReady$.next());
        },
        error: error => this.notificationService.handleError(error),
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(() => this.reloadData());

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<Row>(
      {
        columnDefs: COLUMNS,
        pipes: {
          decimal: this.decimalPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
        rowClassRules: {
          'row-sem-ue-credenciada': params => {
            if (params.node.group) {
              return params.node.allLeafChildren?.some(child => child.data?.verificacao === 'Sem rede U/E credenciada');
            } else {
              return params.data?.verificacao === 'Sem rede U/E credenciada';
            }
          },
        },
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { anoBase, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (anoBase) {
      this.anoBase = +anoBase;
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  private verificarItem(item: Row, cidadesComRedeUE: Set<string>): Row {
    const temRedeUECredenciada = cidadesComRedeUE.has(item.nm_municipio);
    return {
      ...item,
      verificacao: temRedeUECredenciada ? 'Com rede U/E credenciada' : 'Sem rede U/E credenciada',
    };
  }

  reloadData() {
    if (!this.anoBase) {
      this.rowData = [];
      this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
      return;
    }

    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    this.service.getDataGoApi(this.operadora, this.anoBase).subscribe({
      next: items => {
        if (!items?.length) {
          this.rowData = [];
          this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
          return;
        }

        const cidadesComRedeUE = new Set(
          items.filter(item => item.ib_credenciado === 'Sim').map(item => item.nm_municipio),
        );

        this.rowData = items.map(item => this.verificarItem(item, cidadesComRedeUE));
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });
  }

  navYearRight() {
    if (this.anoBase < this.anoAtual) {
      this.anoBase++;
      this.reloadData();
    } else {
      return;
    }
  }

  navYearLeft() {
    if (this.anoBase > this.anoBaseMinimo) {
      this.anoBase--;
      this.reloadData();
    } else {
      return;
    }
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const visaoNome = visao ? `- Visão ${visao?.nm_visao}` : '';

    const fileNamePrefix = `dispersao-urgencia-emergencia-${this.operadoraNome}-${this.anoBase}`;

    const headers = [
      `Dispersão de urgência e emergência ${visaoNome}`,
      `${this.operadoraNome} - ${this.operadora}`,
      'Ano base: ' + this.anoBase,
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      anoBase: this.anoBase,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

export interface Row extends DispersaoUrgenciaEmergencia {
  verificacao?: string;
}
